import { useState, useEffect, useMemo } from 'react';
import EntityLink from '../components/EntityLink';
import { useNavigate } from 'react-router-dom';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton, { EMPTY_PERIOD } from '../components/common/ClearFiltersButton';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';
import HexawarePOImportModal from '../components/HexawarePOImportModal';
import PurchaseOrderEditDrawer from '../components/PurchaseOrderEditDrawer';
import ActionsDropdown from '../components/ActionsDropdown';
import SortTh, { useSort } from '../components/common/SortableHeader';
import PeriodFilter, { usePeriodFilter } from '../components/PeriodFilter';

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pos, setPos] = useState(null);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const search = get('search', '');
  const statusFilter = get('status', 'all');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);
  const [showImport, setShowImport] = useState(false);
  const [showHexImport, setShowHexImport] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const { sortKey, sortDir, handleSort, sortData } = useSort('po_number', 'asc');
  const { period, setPeriod, filterByPeriod } = usePeriodFilter("period_start");

  function load() {
    setError(false);
    api.get('/purchase-orders')
      .then(r => setPos(r.data))
      .catch(() => setError(true));
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!pos) return [];
    const statusSearchFiltered = pos.filter(po => {
      if (search && !po.po_number.toLowerCase().includes(search.toLowerCase()) &&
        !(po.contractor_name || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== 'all' && po.status !== statusFilter) return false;
      return true;
    });
    return filterByPeriod(statusSearchFiltered);
  }, [pos, search, statusFilter, period, filterByPeriod]);

  const sorted = useMemo(() => {
    return sortData(filtered, {
      po_number: (p) => p.po_number || '',
      client_wo_number: (p) => p.client_wo_number || '',
      contractor_name: (p) => p.contractor_name || '',
      period_start: (p) => p.period_start || '',
      sell_rate: (p) => p.sell_rate || 0,
      approved_hours: (p) => p.approved_hours || p.timesheet_hours || 0,
      status: (p) => p.status || '',
    });
  }, [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  function handleFilterChange(key, value) {
    set({ [key]: value, page: 1 });
  }

  async function handleExport() {
    await downloadCsv('/purchase-orders/export', 'purchase-orders-export.csv', exportIdsParam(filtered, pos));
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Purchase Orders {pos && <span className="page-title-count">({pos.length} total)</span>}
          </h1>
          <p className="page-subtitle">Period-specific approvals generated from Work Orders. Create one-at-a-time from a Work Order's detail, or bulk-upload via CSV.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          <button className="btn btn-secondary" onClick={() => navigate('/import-hexaware-pos')}>Import from Hexaware</button>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
        </div>
      </div>

      <div className="filter-bar">
        <PeriodFilter value={period} onChange={setPeriod} />
        <input
          type="text"
          className="filter-search"
          placeholder="Search PO # or contractor…"
          value={search}
          onChange={e => handleFilterChange('search', e.target.value)}
        />
        <select value={statusFilter} onChange={e => handleFilterChange('status', e.target.value)}>
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="invoiced">Invoiced</option>
          <option value="closed">Closed</option>
          <option value="void">Void</option>
        </select>
        <ClearFiltersButton
          active={!!(search || statusFilter !== 'all' || period.from || period.to)}
          onClear={() => { set({ search: '', status: '', page: 1 }); setPeriod(EMPTY_PERIOD); }}
        />
      </div>

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && pos === null && <StateBlock type="loading" />}
      {!error && pos !== null && filtered.length === 0 && (
        <StateBlock type="noResults" message="No Purchase Orders yet. Create one from a Work Order's detail page." />
      )}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <SortTh label="PO Number" sortKey="po_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="WO Number" sortKey="client_wo_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Contractor" sortKey="contractor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Period" sortKey="period_start" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Sell Rate" sortKey="sell_rate" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                <SortTh label="Hours" sortKey="approved_hours" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                <SortTh label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(po => (
                <tr key={po.id}>
                  <td><button className="row-name-link" style={{ fontWeight: 600 }} onClick={() => navigate(`/purchase-orders/${po.id}`)}>{po.po_number}</button></td>
                  <td><EntityLink type="work_order" id={po.wo_id}>{po.client_wo_number || "—"}</EntityLink></td>
                  <td><EntityLink type="contractor" id={po.contractor_id}>{po.contractor_name || "—"}</EntityLink></td>
                  <td>{po.period_start} – {po.period_end}</td>
                  <td>${po.sell_rate}/hr</td>
                  <td>{po.approved_hours || po.timesheet_hours || '—'}</td>
                  <td><Badge>{po.status}</Badge></td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionsDropdown actions={[
                      po.ar_invoice_id
                        ? { label: 'View Invoice', onClick: () => navigate('/ar-invoices', { state: { detail_id: po.ar_invoice_id } }) }
                        : { label: 'Generate Invoice', onClick: () => navigate('/ar-invoices', { state: { po_id: po.id } }) },
                      { label: 'Edit', onClick: () => setEditTarget(po) },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={(p) => set({ page: p })}
            onPageSizeChange={(s) => set({ pageSize: s, page: 1 })}
          />
        </>
      )}

      {showImport && (
        <CsvImportModal
          entityLabel="Purchase Order"
          importPath="/purchase-orders/import"
          onClose={() => setShowImport(false)}
          onComplete={load}
        />
      )}

      {showHexImport && (
        <HexawarePOImportModal
          onClose={() => setShowHexImport(false)}
          onComplete={load}
        />
      )}

      {editTarget && (
        <PurchaseOrderEditDrawer
          po={editTarget}
          isAdmin={user?.role === 'admin'}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}
